// Authentication service for Sanctuary
import csrfService from '../../services/csrfService.js';
import { retryApiCall } from '../../config/supabase.js';
import { frontendLogger } from '../../services/frontendLogger.js';
import { urlConfig } from '../../config/urlConfig.js';

class AuthService {
  constructor() {
    this.baseURL = urlConfig.api.baseURL + '/api';
    this.userChangeListeners = new Set();
    // Request deduplication cache to prevent duplicate API calls
    this.pendingRequests = new Map();
    // Profile cache to prevent excessive /auth/me calls
    this.profileCache = null;
    this.profileCacheTimestamp = null;
    this.profileCacheExpiry = 30000; // 30 seconds
    // Flag to prevent multiple auth redirects
    this.isRedirecting = false;
    // Refresh token lock to prevent race conditions
    this.refreshPromise = null;
  }

  // Subscribe to user changes (including tier changes)
  onUserChange(callback) {
    this.userChangeListeners.add(callback);
    return () => this.userChangeListeners.delete(callback);
  }

  // Deduplicate requests to prevent multiple identical API calls
  async deduplicatedRequest(endpoint, options = {}) {
    const cacheKey = `${endpoint}_${JSON.stringify(options)}`;
    
    // If there's already a pending request for this endpoint, return that promise
    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey);
    }

    // Make new request and cache the promise
    const requestPromise = this.request(endpoint, options);
    this.pendingRequests.set(cacheKey, requestPromise);

    // Clean up cache when request completes (success or failure)
    requestPromise.finally(() => {
      this.pendingRequests.delete(cacheKey);
    });

    return requestPromise;
  }

  // Notify all listeners of user changes
  notifyUserChange(user) {
    this.userChangeListeners.forEach(callback => {
      try {
        callback(user);
      } catch (error) {
        frontendLogger.error('User change callback error', {
          error: error.message,
          stack: error.stack,
          callbackCount: this.userChangeListeners.size
        }, 'authService');
      }
    });
  }

  // Get auth headers
  getAuthHeaders() {
    const token = this.getToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  // Get stored token
  getToken() {
    return localStorage.getItem('sanctuary_token') || localStorage.getItem('authToken');
  }

  // Store token
  setToken(token) {
    localStorage.setItem('sanctuary_token', token);
  }

  // Remove token
  removeToken() {
    localStorage.removeItem('sanctuary_token');
    localStorage.removeItem('authToken');
    localStorage.removeItem('sanctuary_user');
  }

  // Get stored user
  getUser() {
    const userStr = localStorage.getItem('sanctuary_user');
    try {
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  }

  // Store user
  setUser(user) {
    localStorage.setItem('sanctuary_user', JSON.stringify(user));
    // Clear cache when user data changes (except when setting from getProfile)
    if (user !== this.profileCache) {
      this.clearProfileCache();
    }
    this.notifyUserChange(user); // Notify all listeners of the change
  }

  // Remove user
  removeUser() {
    localStorage.removeItem('sanctuary_user');
    this.clearProfileCache();
  }

  // Check if user is authenticated
  isAuthenticated() {
    const token = this.getToken();
    const user = this.getUser();
    return !!(token && user);
  }

  // Lockout utility methods
  isLockoutError(error) {
    return error.isLockout === true || (error.lockoutInfo && error.lockoutInfo.isLocked);
  }

  getLockoutInfo(error) {
    return error.lockoutInfo || null;
  }

  formatLockoutTime(remainingTimeSeconds) {
    if (!remainingTimeSeconds) return '';
    
    const minutes = Math.ceil(remainingTimeSeconds / 60);
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  }

  getLockoutMessage(error) {
    const lockoutInfo = this.getLockoutInfo(error);
    if (!lockoutInfo) return error.message;

    if (lockoutInfo.isLocked && lockoutInfo.remainingTime) {
      const timeFormatted = this.formatLockoutTime(lockoutInfo.remainingTime);
      return `Account temporarily locked due to ${lockoutInfo.attempts} failed login attempts. Please try again in ${timeFormatted} or reset your password if forgotten.`;
    }

    return error.message;
  }

  // Refresh current token using refresh token with race condition prevention
  async refreshToken() {
    // If there's already a refresh in progress, wait for it instead of making a new request
    if (this.refreshPromise) {
      frontendLogger.info('Refresh already in progress, waiting...', {}, 'authService');
      return this.refreshPromise;
    }

    // Create and store the refresh promise to prevent duplicate requests
    this.refreshPromise = (async () => {
      try {
        // Read the refresh token INSIDE the lock to get the latest value
        const refreshToken = localStorage.getItem('sanctuary_refresh_token') || 
                            localStorage.getItem('refresh_token');
        
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        const response = await this.request('/auth/refresh', {
          method: 'POST',
          body: JSON.stringify({ refresh_token: refreshToken })
        });

        if (response.access_token) {
          this.setToken(response.access_token);
          if (response.refresh_token) {
            // CRITICAL: Update the refresh token immediately for next call
            localStorage.setItem('sanctuary_refresh_token', response.refresh_token);
          }
          return response;
        }

        throw new Error('Failed to refresh token');
      } catch (error) {
        // If refresh fails with "already used", log it for debugging
        if (error.message?.includes('already') || error.message?.includes('Invalid')) {
          frontendLogger.error('Refresh token already used or invalid', {
            error: error.message
          }, 'authService');
        }
        throw error;
      } finally {
        // Clear the lock after refresh completes (success or failure)
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  // Enhanced login with lockout handling and bot protection
  async loginWithLockoutHandling({ email, password, formStartTime, interactionCount, keyboardEvents, mouseEvents, focusEvents }) {
    try {
      return await this.login({ 
        email, 
        password,
        // Include bot protection data
        formStartTime,
        interactionCount,
        keyboardEvents,
        mouseEvents,
        focusEvents
      });
    } catch (error) {
      // If it's a lockout error, enhance the error with formatted message
      if (this.isLockoutError(error)) {
        const enhancedError = new Error(this.getLockoutMessage(error));
        enhancedError.isLockout = true;
        enhancedError.lockoutInfo = this.getLockoutInfo(error);
        throw enhancedError;
      }
      
      // For other errors, pass through as-is
      throw error;
    }
  }

  // API request helper
  async request(endpoint, options = {}) {
    // For POST requests, use CSRF service
    if (options.method === 'POST') {
      // If body is already a string, parse it; if it's an object, use it directly
      const body = typeof options.body === 'string' ? JSON.parse(options.body) : options.body || {};
      
      try {
        return await csrfService.post(endpoint, body);
      } catch (error) {
        // Handle specific auth endpoint errors with lockout info
        if ((endpoint === '/auth/signin' || endpoint === '/auth/2fa/verify-login') && error.lockoutInfo) {
          // Lockout info is already attached by csrfService
        }
        throw error;
      }
    }

    // For PATCH requests, use CSRF service
    if (options.method === 'PATCH') {
      // If body is already a string, parse it; if it's an object, use it directly
      const body = typeof options.body === 'string' ? JSON.parse(options.body) : options.body || {};
      
      try {
        return await csrfService.patch(endpoint, body);
      } catch (error) {
        frontendLogger.error('Auth API Error (PATCH request)', {
          error: error.message,
          stack: error.stack,
          endpoint,
          method: 'PATCH'
        }, 'authService');
        throw error;
      }
    }

    // For GET requests, use regular fetch with auth headers
    const url = `${this.baseURL}${endpoint}`;
    
    const config = {
      headers: this.getAuthHeaders(),
      ...options
    };

    try {
      const response = await fetch(url, config);
      
      if (response.status === 401) {
        // Check if we're already redirecting to prevent multiple redirects
        if (this.isRedirecting) {
          throw new Error('Authentication redirect in progress');
        }

        // Check if we have a token to refresh
        const currentToken = this.getToken();
        if (!currentToken) {
          // No token available, redirect to login immediately
          this.isRedirecting = true;
          this.removeToken(); // Clean up any stale data
          window.location.href = '/login?reason=no_token';
          return;
        }

        // Use retry logic before giving up
        try {
          return await retryApiCall(async () => {
            // Import session manager dynamically to avoid circular imports
            const { sessionManager } = await import('../../config/supabase.js');
            const isValid = await sessionManager.checkSessionValidity();
            
            if (isValid) {
              // Token was refreshed, retry the original request with new headers
              const newConfig = {
                ...config,
                headers: this.getAuthHeaders() // Get fresh headers with new token
              };
              
              const retryResponse = await fetch(url, newConfig);
              if (retryResponse.ok) {
                return await retryResponse.json();
              } else if (retryResponse.status === 401) {
                throw new Error('Authentication failed after refresh');
              }
            }
            throw new Error('Session refresh failed');
          }, 2, 1000); // 2 retries with 1 second delay
        } catch {
          // Only log out after retries are exhausted
          this.isRedirecting = true;
          this.removeToken(); // Clean up tokens before redirect
          window.location.href = '/login?reason=auth_failed';
          return;
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      frontendLogger.error('Auth API Error (regular request)', {
        error: error.message,
        stack: error.stack,
        endpoint,
        method: options.method || 'GET'
      }, 'authService');
      throw error;
    }
  }

  // Register new user
  async register({ name, full_name, email, password, date_of_birth, invite_code_used }) {
    const registrationData = {
      email,
      password,
      full_name,
      date_of_birth,
      invite_code_used,
      metadata: { display_name: name }
    };

    const response = await this.request('/auth/sign-up', {
      method: 'POST',
      body: JSON.stringify(registrationData)
    });

    return response;
  }

  // Send registration email verification (public endpoint - no auth required)
  async sendRegistrationEmailVerification(email) {
    const response = await csrfService.post('/auth/registration/send-verification', { email });
    return response;
  }

  // Verify registration email with code (public endpoint - no auth required)
  async verifyRegistrationEmail(email, code) {
    const response = await csrfService.post('/auth/registration/verify-email', { email, code });
    return response;
  }

  // Sign in user
  async login({ email, password }) {
    const response = await this.request('/auth/signin', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    });

    // Check if 2FA is required
    if (response.requires_2fa || response.requires2FA) {
      return {
        requires2FA: true,
        tempToken: response.tempToken,
        twoFactorMethod: response.two_factor_method,
        tempSessionId: response.temp_session_id
      };
    }

    // Handle normal login completion
    if (response.access_token && response.user) {
      this.setToken(response.access_token);
      
      // Store refresh token for future token refresh
      if (response.refresh_token) {
        localStorage.setItem('sanctuary_refresh_token', response.refresh_token);
      }
      
      // Store basic user data first
      this.setUser({
        id: response.user.id,
        email: response.user.email,
        ...response.user.user_metadata
      });

      // Then fetch and merge profile data to get tier and other info
      try {
        await this.getProfile();
      } catch (error) {
        frontendLogger.warn('Failed to fetch profile during regular login', {
          error: error.message,
          stack: error.stack,
          userId: this.user?.id || 'unknown'
        }, 'authService');
        // Don't fail login if profile fetch fails
      }
    }

    return response;
  }

  // Verify 2FA during login
  async verify2FALogin({ temp_session_id, code }) {
    const response = await this.request('/auth/2fa/verify-login', {
      method: 'POST',
      body: JSON.stringify({ temp_session_id, code })
    });

    // Handle successful 2FA verification
    if (response.access_token && response.user) {
      this.setToken(response.access_token);
      
      // Store refresh token for future token refresh
      if (response.refresh_token) {
        localStorage.setItem('sanctuary_refresh_token', response.refresh_token);
      }
      
      // Store basic user data first
      this.setUser({
        id: response.user.id,
        email: response.user.email,
        ...response.user.user_metadata
      });

      // Then fetch and merge profile data to get tier and other info
      try {
        await this.getProfile();
      } catch (error) {
        frontendLogger.warn('Failed to fetch profile during 2FA login', {
          error: error.message,
          stack: error.stack,
          userId: this.user?.id || 'unknown'
        }, 'authService');
        // Don't fail login if profile fetch fails
      }
    }

    return response;
  }

  // Send email code during login (for email 2FA)
  async sendLoginEmailCode({ temp_session_id }) {
    const response = await this.request('/auth/2fa/send-login-code', {
      method: 'POST',
      body: JSON.stringify({ temp_session_id })
    });
    return response;
  }

  // Get current user profile
  async getProfile() {
    // Check if we have a valid cached profile
    const now = Date.now();
    if (this.profileCache && 
        this.profileCacheTimestamp && 
        (now - this.profileCacheTimestamp) < this.profileCacheExpiry) {
      return this.profileCache;
    }

    const response = await this.deduplicatedRequest('/auth/me');
    
    if (response.user && response.profile) {
      // Merge auth user with profile data
      const userData = {
        id: response.user.id,
        email: response.user.email,
        display_name: response.profile.display_name,
        current_tier: response.profile.current_tier || 'seeker',
        onboarding_answers: response.profile.onboarding_answers,
        preferences: response.profile.preferences,
        active_archetype: response.profile.active_archetype,
        created_at: response.profile.created_at,
        avatar_url: response.profile.avatar_url,
        is_active: response.profile.is_active,
        last_login_at: response.profile.last_login_at
      };
      
      // Update cache
      this.profileCache = userData;
      this.profileCacheTimestamp = now;
      
      this.setUser(userData);
      return userData;
    }

    return response;
  }

  // Force refresh profile bypassing cache
  async refreshProfile() {
    this.clearProfileCache();
    return await this.getProfile();
  }

  // Update user profile
  async updateProfile(updates) {
    const response = await this.request('/auth/profile', {
      method: 'PATCH',
      body: JSON.stringify(updates)
    });

    if (response.profile) {
      // Clear cache and update stored user data
      this.clearProfileCache();
      const currentUser = this.getUser();
      const updatedUser = {
        ...currentUser,
        ...response.profile
      };
      this.setUser(updatedUser);
    }

    return response;
  }

  // Complete onboarding
  async completeOnboarding(onboardingData) {
    const response = await this.request('/onboarding/complete', {
      method: 'POST',
      body: JSON.stringify({
        device_id: this.getDeviceId(),
        user_id: this.getUser()?.id,
        onboarding_data: onboardingData
      })
    });

    // Update profile with onboarding completion
    const archetypeId = onboardingData.selectedArchetype?.archetype_id || onboardingData.selectedArchetype?.id || onboardingData.archetypeId;
    await this.updateProfile({
      onboarding_answers: onboardingData,
      current_tier: onboardingData.selectedTier || 'seeker',
      active_archetype: archetypeId?.toString(),
      ritual_timing_preference: onboardingData.ritualPreferences?.timing,
      ritual_tone_preference: onboardingData.ritualPreferences?.tone,
      preferences: {
        goals: onboardingData.goalObjects || onboardingData.goals,
        goal_ids: onboardingData.goals,
        ritual_preferences: onboardingData.ritualPreferences,
        completed_onboarding: true,
        completed_at: new Date().toISOString()
      }
    });

    return response;
  }

  // Set initial archetype
  async setArchetype(archetypeId) {
    const response = await this.request('/onboarding/choose-archetype', {
      method: 'POST',
      body: JSON.stringify({ archetype_id: archetypeId })
    });

    return response;
  }

  // Check onboarding status
  hasCompletedOnboarding() {
    const user = this.getUser();
    return user?.preferences?.completed_onboarding || false;
  }

  // Check if onboarding is currently active (user is in the process)
  isOnboardingActive() {
    // Check if user exists but hasn't completed onboarding
    const user = this.getUser();
    const hasUser = !!user;
    const hasCompletedOnboarding = this.hasCompletedOnboarding();
    
    // Onboarding is active if user exists but hasn't completed onboarding
    return hasUser && !hasCompletedOnboarding;
  }

  // Get user's current tier
  getCurrentTier() {
    const user = this.getUser();
    return user?.current_tier || 'seeker';
  }

  // Logout user
  async logout() {
    // Check if we have a token for backend logout
    const token = this.getToken();
    
    if (token) {
      try {
        // Call backend logout endpoint to properly invalidate session
        await this.request('/auth/user/logout', {
          method: 'POST'
        });
      } catch (error) {
        frontendLogger.warn('Backend logout failed, continuing with local cleanup', {
          error: error.message,
          stack: error.stack,
          hasToken: !!this.token
        }, 'authService');
        // Continue with local cleanup even if backend call fails
      }
    } else {
      // Perform local cleanup only when no token
    }
    
    // Clear local storage and caches
    this.removeToken();
    this.removeUser();
    this.clearProfileCache();
  }

  // Clear profile cache
  clearProfileCache() {
    this.profileCache = null;
    this.profileCacheTimestamp = null;
  }

  // Generate device ID for anonymous tracking
  getDeviceId() {
    let deviceId = localStorage.getItem('sanctuary_device_id');
    if (!deviceId) {
      deviceId = 'dev_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
      localStorage.setItem('sanctuary_device_id', deviceId);
    }
    return deviceId;
  }

  // Track anonymous interaction
  async trackAnonymousInteraction(interactionType, contentType = null, contentId = null) {
    try {
      await this.request('/onboarding/track-interaction', {
        method: 'POST',
        body: JSON.stringify({
          device_id: this.getDeviceId(),
          interaction_type: interactionType,
          content_type: contentType,
          content_id: contentId
        })
      });
    } catch (error) {
      frontendLogger.warn('Failed to track anonymous interaction', {
        error: error.message,
        stack: error.stack,
        interactionType,
        contentType,
        contentId
      }, 'authService');
    }
  }

  // Get subscription status
  async getSubscription() {
    const response = await this.deduplicatedRequest('/subscriptions/me');
    return response;
  }

  // Upgrade subscription tier
  async upgradeTier(tier) {
    const normalizedTier = tier?.toLowerCase();
    const response = await this.request('/subscriptions/upgrade', {
      method: 'POST',
      body: JSON.stringify({ newTier: normalizedTier })
    });

    // Update user's current tier in storage
    if (response.profile) {
      const currentUser = this.getUser();
      this.setUser({
        ...currentUser,
        current_tier: tier
      });
    }

    return response;
  }

  // Cancel subscription
  async cancelSubscription() {
    return await this.request('/subscriptions/cancel', {
      method: 'POST'
    });
  }

  // Reactivate subscription
  async reactivateSubscription(tier) {
    const normalizedTier = tier?.toLowerCase();
    return await this.request('/subscriptions/reactivate', {
      method: 'POST',
      body: JSON.stringify({ tier: normalizedTier })
    });
  }

  // Update auto-renewal setting
  async updateAutoRenew(auto_renew) {
    return await this.request('/subscriptions/auto-renew', {
      method: 'POST',
      body: JSON.stringify({ auto_renew })
    });
  }

  // Request password reset email
  async requestPasswordReset(email) {
    try {
      const response = await csrfService.post('/password-reset/reset-password', { email });
      return response;
    } catch (error) {
      frontendLogger.error('Password reset request error', {
        error: error.message,
        stack: error.stack,
        email: email ? '[REDACTED]' : 'undefined'
      }, 'authService');
      throw error;
    }
  }

  // Confirm password reset with token
  async confirmPasswordReset(token, password) {
    try {
      const response = await csrfService.post('/password-reset/reset-password/confirm', { token, password });
      return response;
    } catch (error) {
      frontendLogger.error('Password reset confirmation error', {
        error: error.message,
        stack: error.stack,
        hasToken: !!token
      }, 'authService');
      throw error;
    }
  }

  // Format error messages for user display
  formatError(error) {
    if (error.message) {
      // Common error message formatting
      if (error.message.includes('Invalid credentials')) {
        return 'Invalid email or password. Please check and try again.';
      }
      if (error.message.includes('Email not confirmed')) {
        return 'Please check your email and confirm your account before signing in.';
      }
      if (error.message.includes('Invalid email format')) {
        return 'Please enter a valid email address.';
      }
      if (error.message.includes('Password must be')) {
        return error.message; // Password validation errors are already user-friendly
      }
      if (error.message.includes('Invalid or expired')) {
        return 'This reset link is invalid or has expired. Please request a new one.';
      }
      return error.message;
    }
    return 'An unexpected error occurred. Please try again.';
  }
}

export default new AuthService();
